/**
 * @file The sheet a favorite is saved or edited in. Saving and deleting dispatch `favorite:saved` and
 * `favorite:deleted` on its form, so whoever opened the sheet updates its own markup.
 */
import { createFeedbackDialog } from './feedback-dialog-module.js';
import * as loadingMask from './load-mask.js';
import { getSheet } from './sheet-overlay.js';
import { postAction, runWithTriggerDisabled } from './utils/action-endpoint.js';

export const EVENT_SAVED = 'favorite:saved';
export const EVENT_DELETED = 'favorite:deleted';

/**
 * The server's message may contain a link.
 *
 * @param {HTMLFormElement} form
 * @param {Error & {response?: {message?: string}}} failure
 */
function showFailure(form, failure) {
  const message = failure.response?.message;
  if (message) {
    createFeedbackDialog('feedback_error', message);
  } else {
    form.querySelector('.js_form_error').textContent = form.dataset.messageError;
  }
}

/**
 * @param {HTMLFormElement} form
 */
function closeSheetOf(form) {
  getSheet(form.closest('dialog').id)?.close();
}

/**
 * @param {HTMLFormElement} form
 * @param {string} type
 * @param {number} favoriteId
 */
function announce(form, type, favoriteId) {
  form.dispatchEvent(new CustomEvent(type, { bubbles: true, detail: { favoriteId } }));
}

/**
 * @param {HTMLFormElement} form
 * @param {HTMLButtonElement} submitter
 */
function save(form, submitter) {
  form.querySelector('.js_form_error').textContent = '';

  return runWithTriggerDisabled(
    submitter,
    async () => {
      const data = await postAction(form.dataset.actionUrl, new FormData(form));
      closeSheetOf(form);
      createFeedbackDialog('feedback_success', form.dataset.messageSaved);
      announce(form, EVENT_SAVED, Number(data.id));
    },
    (failure) => showFailure(form, failure),
  );
}

/**
 * @param {HTMLButtonElement} trigger
 */
function remove(trigger) {
  const form = trigger.form;
  const favoriteId = form.elements.namedItem('id').value;

  return runWithTriggerDisabled(
    trigger,
    async () => {
      await postAction(trigger.dataset.actionUrl, new URLSearchParams({ id: favoriteId }));
      closeSheetOf(form);
      createFeedbackDialog('feedback_success', form.dataset.messageDeleted);
      announce(form, EVENT_DELETED, Number(favoriteId));
    },
    (failure) => showFailure(form, failure),
  );
}

/**
 * @param {HTMLFormElement} form
 * @param {boolean} isAsking
 */
function showDeleteConfirmation(form, isAsking) {
  form.querySelector('.js_favorite_edit_actions').hidden = isAsking;
  form.querySelector('.js_favorite_delete_confirm').hidden = !isAsking;
}

/**
 * @param {HTMLFormElement} form
 * @param {boolean} isAsking
 */
function toggleDeleteConfirmation(form, isAsking) {
  showDeleteConfirmation(form, isAsking);
  form.querySelector(isAsking ? '.js_favorite_delete_keep' : '.js_favorite_delete_ask').focus();
}

/**
 * Opens a sheet already on the page as last saved, dropping unsaved picks.
 *
 * @param {string} sheetId
 */
export function reopenFavoriteSheet(sheetId) {
  const form = document.getElementById(sheetId).querySelector('.js_favorite_edit_form');
  form.reset();
  form.querySelector('.js_form_error').textContent = '';
  showDeleteConfirmation(form, false);
  getSheet(sheetId).open();
}

/**
 * Handles every favorite sheet on the page. Each entry bundles its own copy of this module, so only the first one
 * to run registers the listeners.
 */
export function wireFavoriteSheets() {
  const root = document.documentElement;
  if (root.dataset.favoriteSheets === 'wired') {
    return;
  }
  root.dataset.favoriteSheets = 'wired';

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.js_favorite_edit_form');
    if (form) {
      event.preventDefault();
      save(form, event.submitter);
    }
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('.js_favorite_edit_form button');
    if (trigger?.matches('.js_favorite_delete_ask')) {
      toggleDeleteConfirmation(trigger.form, true);
    } else if (trigger?.matches('.js_favorite_delete_keep')) {
      toggleDeleteConfirmation(trigger.form, false);
    } else if (trigger?.matches('.js_favorite_delete')) {
      remove(trigger);
    }
  });
}

/**
 * Loads the sheet for a product and opens it; resolves to its form, or null if the product cannot be saved.
 *
 * @param {HTMLElement} trigger carries the sheet's `data-sheet-url` and the `data-message-error` for a failed load
 * @param {URLSearchParams} body the product's `articleTypeId` and the selection to start from
 * @returns {Promise<HTMLFormElement|null>}
 */
export async function openFavoriteSheet(trigger, body) {
  wireFavoriteSheets();

  let html;
  loadingMask.show();
  try {
    ({ html } = await postAction(trigger.dataset.sheetUrl, body));
  } catch (failure) {
    createFeedbackDialog('feedback_error', failure.response?.message ?? trigger.dataset.messageError);
    return null;
  } finally {
    loadingMask.hide();
  }

  const template = document.createElement('template');
  template.innerHTML = html;
  const sheet = template.content.querySelector('sheet-overlay');
  if (!sheet) {
    createFeedbackDialog('feedback_error', trigger.dataset.messageError);
    return null;
  }

  const dialogId = sheet.querySelector('dialog').id;
  document.getElementById(dialogId)?.closest('sheet-overlay')?.remove();
  document.body.append(sheet);
  getSheet(dialogId)?.open();

  return sheet.querySelector('.js_favorite_edit_form');
}
